/**
 * Operation log component for displaying audit trail.
 */

import { useState } from 'react';
import { Table, Tag, Typography, Empty, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatDate } from '@/utils';
import { t as tGlobal, useT } from '@/i18n';
import type { OperationLog as OperationLogType } from '@/types';

const { Text } = Typography;

// File change types
interface FileChange {
  id: string;
  action: '+' | '-' | '~';
  file_type: string;
  file_name: string;
  file_size: number;
  source_type?: 'upload' | 'git' | 'select';
  git_url?: string;
  git_branch?: string;
  git_commit?: string;
  git_tag?: string;
  file_source?: string;
  content_before?: string;  // 修改前的文字内容（用于显示文字内容变化）
  content_after?: string;   // 修改后的文字内容（用于显示文字内容变化）
}

interface FileChangeSummary {
  added: number;
  deleted: number;
  updated: number;
}

// Re-export for backward compatibility
export type LogEntry = OperationLogType;

interface OperationLogProps {
  logs: OperationLogType[];
  loading?: boolean;
}

const actionColors: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  download: 'cyan',
  auto_delete: 'orange',
};

const sourceColors: Record<string, string> = {
  bsp: 'blue',
  api: 'purple',
  bvp_bot: 'green',
  rpa: 'orange',
  system: 'default',
};

/**
 * 解析文件变更摘要（包括 files 数组 + release_note + test_report + 单文件操作）
 */
const getFileChangeSummary = (record: OperationLogType): FileChangeSummary | null => {
  // 处理单文件操作日志
  if (record.target_type === 'file') {
    if (record.action === 'delete') {
      return { added: 0, deleted: 1, updated: 0 };
    }
    if (record.action === 'create') {
      return { added: 1, deleted: 0, updated: 0 };
    }
    if (record.action === 'update') {
      return { added: 0, deleted: 0, updated: 1 };
    }
    return null;
  }

  // 处理包级别/对外释放文件级别操作日志
  if (record.target_type !== 'package' && record.target_type !== 'external_release') return null;
  if (!['create', 'update'].includes(record.action)) return null;

  const beforeFiles = (record.before_data?.files as any[]) || [];
  const afterFiles = (record.after_data?.files as any[]) || [];

  let added = 0;
  let deleted = 0;
  let updated = 0;

  // 对于 create 操作，所有文件都是新增
  if (record.action === 'create') {
    added = afterFiles.length;

    // 检查 Release Note（文件和文字内容独立计数）
    if (record.after_data?.release_note_url || record.after_data?.release_note_name) { added++; }
    if (record.after_data?.release_note_content) { added++; }

    // 检查测试报告（文件和文字内容独立计数）
    if (record.after_data?.test_report_url || record.after_data?.test_report_name) { added++; }
    if (record.after_data?.test_report_content) { added++; }

    // 检查 Momenta Release Note（文件和文字内容独立计数）
    if (record.after_data?.momenta_release_note_url || record.after_data?.momenta_release_note_name) { added++; }
    if (record.after_data?.momenta_release_note_content) { added++; }

    // 检查 Momenta 测试报告（文件和文字内容独立计数）
    if (record.after_data?.momenta_test_report_url || record.after_data?.momenta_test_report_name) { added++; }
    if (record.after_data?.momenta_test_report_content) { added++; }

    if (added > 0) {
      return { added, deleted: 0, updated: 0 };
    }
    return null;
  }

  // 对于 update 操作，比较前后文件
  // 使用 checksum 作为唯一标识（如果没有则使用 file_name）
  const getFileKey = (f: any) => {
    const key = f.checksum || f.file_name;
    return key ? String(key).trim() : '';
  };

  const beforeKeys = new Set(beforeFiles.map(getFileKey).filter(k => k));
  const afterKeys = new Set(afterFiles.map(getFileKey).filter(k => k));

  added = afterFiles.filter(f => {
    const key = getFileKey(f);
    return key && !beforeKeys.has(key);
  }).length;

  deleted = beforeFiles.filter(f => {
    const key = getFileKey(f);
    return key && !afterKeys.has(key);
  }).length;

  // 检查 Release Note 变更（支持平级和嵌套两种格式）
  // 注意：文件和文字内容独立检测

  // 1. 检测文件变化（URL 或 name 存在）
  const hasBeforeRNFile = Boolean(
    (record.before_data?.release_note_url && String(record.before_data.release_note_url).trim())
    || (record.before_data?.release_note_name && String(record.before_data.release_note_name).trim())
    || ((record.before_data as any)?.release_note?.release_note_url && String((record.before_data as any).release_note.release_note_url).trim())
    || ((record.before_data as any)?.release_note?.release_note_name && String((record.before_data as any).release_note.release_note_name).trim())
  );

  const hasAfterRNFile = Boolean(
    (record.after_data?.release_note_url && String(record.after_data.release_note_url).trim())
    || (record.after_data?.release_note_name && String(record.after_data.release_note_name).trim())
    || ((record.after_data as any)?.release_note?.release_note_url && String((record.after_data as any).release_note.release_note_url).trim())
    || ((record.after_data as any)?.release_note?.release_note_name && String((record.after_data as any).release_note.release_note_name).trim())
  );

  // 2. 检测文字内容变化（独立检测，不受文件状态影响）
  const hasBeforeRNContent = Boolean(
    (record.before_data?.release_note_content && String(record.before_data.release_note_content).trim())
    || ((record.before_data as any)?.release_note?.release_note_content && String((record.before_data as any).release_note.release_note_content).trim())
  );

  const hasAfterRNContent = Boolean(
    (record.after_data?.release_note_content && String(record.after_data.release_note_content).trim())
    || ((record.after_data as any)?.release_note?.release_note_content && String((record.after_data as any).release_note.release_note_content).trim())
  );

  // 3. 文件变化统计
  if (!hasBeforeRNFile && hasAfterRNFile) {
    added++;  // 新增文件
  } else if (hasBeforeRNFile && !hasAfterRNFile) {
    deleted++;  // 删除文件
  } else if (hasBeforeRNFile && hasAfterRNFile) {
    // 替换文件：比较文件名（after 可能是 deferred，url 为空，用 name 判断）
    const beforeRNName = record.before_data?.release_note_name || record.before_data?.release_note_url || (record.before_data?.release_note as any)?.release_note_name || '';
    const afterRNName = record.after_data?.release_note_name || record.after_data?.release_note_url || (record.after_data?.release_note as any)?.release_note_name || '';
    if (beforeRNName !== afterRNName) {
      deleted++;  // 旧文件
      added++;    // 新文件
    } else {
      const beforeURL = record.before_data?.release_note_url || (record.before_data?.release_note as any)?.release_note_url || '';
      const afterURL = record.after_data?.release_note_url || (record.after_data?.release_note as any)?.release_note_url || '';
      if (beforeURL !== afterURL) updated++;
    }
  }

  // 4. 文字内容变化统计
  if (!hasBeforeRNContent && hasAfterRNContent) {
    added++;  // 新增文字内容
  } else if (hasBeforeRNContent && !hasAfterRNContent) {
    deleted++;  // 删除文字内容
  } else if (hasBeforeRNContent && hasAfterRNContent) {
    // 检查内容是否变化
    const beforeContent = record.before_data?.release_note_content || (record.before_data?.release_note as any)?.release_note_content || '';
    const afterContent = record.after_data?.release_note_content || (record.after_data?.release_note as any)?.release_note_content || '';
    if (beforeContent !== afterContent) {
      updated++;  // 修改文字内容
    }
  }

  // 检查测试报告变更（支持平级和嵌套两种格式）
  // 注意：文件和文字内容独立检测

  // 1. 检测文件变化（URL 或 name 存在）
  const trBeforeList = (record.before_data as any)?.test_report_files;
  const trAfterList = (record.after_data as any)?.test_report_files;
  const hasBeforeTRFile = Boolean(
    (record.before_data?.test_report_url && String(record.before_data.test_report_url).trim())
    || (record.before_data?.test_report_name && String(record.before_data.test_report_name).trim())
    || ((record.before_data as any)?.test_report?.test_report_url && String((record.before_data as any).test_report.test_report_url).trim())
    || ((record.before_data as any)?.test_report?.test_report_name && String((record.before_data as any).test_report.test_report_name).trim())
  );

  const hasAfterTRFile = Boolean(
    (record.after_data?.test_report_url && String(record.after_data.test_report_url).trim())
    || (record.after_data?.test_report_name && String(record.after_data.test_report_name).trim())
    || ((record.after_data as any)?.test_report?.test_report_url && String((record.after_data as any).test_report.test_report_url).trim())
    || ((record.after_data as any)?.test_report?.test_report_name && String((record.after_data as any).test_report.test_report_name).trim())
  );

  // 2. 检测文字内容变化（独立检测，不受文件状态影响）
  const hasBeforeTRContent = Boolean(
    (record.before_data?.test_report_content && String(record.before_data.test_report_content).trim())
    || ((record.before_data as any)?.test_report?.test_report_content && String((record.before_data as any).test_report.test_report_content).trim())
  );

  const hasAfterTRContent = Boolean(
    (record.after_data?.test_report_content && String(record.after_data.test_report_content).trim())
    || ((record.after_data as any)?.test_report?.test_report_content && String((record.after_data as any).test_report.test_report_content).trim())
  );

  // 3. 文件变化统计
  if (Array.isArray(trBeforeList) || Array.isArray(trAfterList)) {
    const keyOf = (f: { url?: string; name?: string }) => f.url || f.name || '';
    const beforeKeys = new Set<string>((trBeforeList || []).map((f: any) => keyOf(f)).filter(Boolean));
    const afterKeys = new Set<string>((trAfterList || []).map((f: any) => keyOf(f)).filter(Boolean));
    afterKeys.forEach((k: string) => { if (!beforeKeys.has(k)) added++; });
    beforeKeys.forEach((k: string) => { if (!afterKeys.has(k)) deleted++; });
  } else if (!hasBeforeTRFile && hasAfterTRFile) {
    added++;  // 新增文件
  } else if (hasBeforeTRFile && !hasAfterTRFile) {
    deleted++;  // 删除文件
  } else if (hasBeforeTRFile && hasAfterTRFile) {
    const beforeTRName = record.before_data?.test_report_name || record.before_data?.test_report_url || (record.before_data?.test_report as any)?.test_report_name || '';
    const afterTRName = record.after_data?.test_report_name || record.after_data?.test_report_url || (record.after_data?.test_report as any)?.test_report_name || '';
    if (beforeTRName !== afterTRName) {
      deleted++;
      added++;
    } else {
      const beforeURL = record.before_data?.test_report_url || (record.before_data?.test_report as any)?.test_report_url || '';
      const afterURL = record.after_data?.test_report_url || (record.after_data?.test_report as any)?.test_report_url || '';
      if (beforeURL !== afterURL) updated++;
    }
  }

  // 4. 文字内容变化统计
  if (!hasBeforeTRContent && hasAfterTRContent) {
    added++;  // 新增文字内容
  } else if (hasBeforeTRContent && !hasAfterTRContent) {
    deleted++;  // 删除文字内容
  } else if (hasBeforeTRContent && hasAfterTRContent) {
    // 检查内容是否变化
    const beforeContent = record.before_data?.test_report_content || (record.before_data?.test_report as any)?.test_report_content || '';
    const afterContent = record.after_data?.test_report_content || (record.after_data?.test_report as any)?.test_report_content || '';
    if (beforeContent !== afterContent) {
      updated++;  // 修改文字内容
    }
  }

  // 检查 Momenta Release Note 变更
  const hasBeforeMRNFile = Boolean(
    (record.before_data?.momenta_release_note_url && String(record.before_data.momenta_release_note_url).trim())
    || (record.before_data?.momenta_release_note_name && String(record.before_data.momenta_release_note_name).trim())
  );
  const hasAfterMRNFile = Boolean(
    (record.after_data?.momenta_release_note_url && String(record.after_data.momenta_release_note_url).trim())
    || (record.after_data?.momenta_release_note_name && String(record.after_data.momenta_release_note_name).trim())
  );
  const hasBeforeMRNContent = Boolean(record.before_data?.momenta_release_note_content && String(record.before_data.momenta_release_note_content).trim());
  const hasAfterMRNContent = Boolean(record.after_data?.momenta_release_note_content && String(record.after_data.momenta_release_note_content).trim());

  if (!hasBeforeMRNFile && hasAfterMRNFile) { added++; }
  else if (hasBeforeMRNFile && !hasAfterMRNFile) { deleted++; }
  else if (hasBeforeMRNFile && hasAfterMRNFile) {
    const bMRNName = record.before_data?.momenta_release_note_name || record.before_data?.momenta_release_note_url || '';
    const aMRNName = record.after_data?.momenta_release_note_name || record.after_data?.momenta_release_note_url || '';
    if (bMRNName !== aMRNName) { deleted++; added++; }
    else if (record.before_data?.momenta_release_note_url !== record.after_data?.momenta_release_note_url) updated++;
  }
  if (!hasBeforeMRNContent && hasAfterMRNContent) { added++; }
  else if (hasBeforeMRNContent && !hasAfterMRNContent) { deleted++; }
  else if (hasBeforeMRNContent && hasAfterMRNContent) {
    if (record.before_data?.momenta_release_note_content !== record.after_data?.momenta_release_note_content) updated++;
  }

  // 检查 Momenta 测试报告变更
  const mtrBeforeList = (record.before_data as any)?.momenta_test_report_files;
  const mtrAfterList = (record.after_data as any)?.momenta_test_report_files;
  const hasBeforeMTRFile = Boolean(
    (record.before_data?.momenta_test_report_url && String(record.before_data.momenta_test_report_url).trim())
    || (record.before_data?.momenta_test_report_name && String(record.before_data.momenta_test_report_name).trim())
  );
  const hasAfterMTRFile = Boolean(
    (record.after_data?.momenta_test_report_url && String(record.after_data.momenta_test_report_url).trim())
    || (record.after_data?.momenta_test_report_name && String(record.after_data.momenta_test_report_name).trim())
  );
  const hasBeforeMTRContent = Boolean(record.before_data?.momenta_test_report_content && String(record.before_data.momenta_test_report_content).trim());
  const hasAfterMTRContent = Boolean(record.after_data?.momenta_test_report_content && String(record.after_data.momenta_test_report_content).trim());

  if (Array.isArray(mtrBeforeList) || Array.isArray(mtrAfterList)) {
    const keyOf = (f: { url?: string; name?: string }) => f.url || f.name || '';
    const beforeKeys = new Set<string>((mtrBeforeList || []).map((f: any) => keyOf(f)).filter(Boolean));
    const afterKeys = new Set<string>((mtrAfterList || []).map((f: any) => keyOf(f)).filter(Boolean));
    afterKeys.forEach((k) => { if (!beforeKeys.has(k)) added++; });
    beforeKeys.forEach((k) => { if (!afterKeys.has(k)) deleted++; });
  } else if (!hasBeforeMTRFile && hasAfterMTRFile) { added++; }
  else if (hasBeforeMTRFile && !hasAfterMTRFile) { deleted++; }
  else if (hasBeforeMTRFile && hasAfterMTRFile) {
    const bMTRName = record.before_data?.momenta_test_report_name || record.before_data?.momenta_test_report_url || '';
    const aMTRName = record.after_data?.momenta_test_report_name || record.after_data?.momenta_test_report_url || '';
    if (bMTRName !== aMTRName) { deleted++; added++; }
    else if (record.before_data?.momenta_test_report_url !== record.after_data?.momenta_test_report_url) updated++;
  }
  if (!hasBeforeMTRContent && hasAfterMTRContent) { added++; }
  else if (hasBeforeMTRContent && !hasAfterMTRContent) { deleted++; }
  else if (hasBeforeMTRContent && hasAfterMTRContent) {
    if (record.before_data?.momenta_test_report_content !== record.after_data?.momenta_test_report_content) updated++;
  }

  if (added === 0 && deleted === 0 && updated === 0) {
    return null;
  }

  return { added, deleted, updated };
};

/**
 * 获取文件类型显示名称
 */
const getFileTypeName = (file: any): string => {
  // 优先使用 file_type.name（业务类型，如 MCU、SOC、CAN_ARXML 等）
  if (file.file_type?.name) {
    return file.file_type.name;
  }
  // Fallback：历史数据可能没有 file_type
  return '-';
};

/**
 * 解析文件变更详情（包括 files 数组 + release_note + test_report + 单文件操作）
 */
const getFileChanges = (record: OperationLogType): FileChange[] => {
  // 处理单文件操作日志
  if (record.target_type === 'file') {
    const data = record.action === 'delete' ? record.before_data : record.after_data;
    if (!data) return [];

    return [{
      id: String(record.target_id),
      action: record.action === 'delete' ? '-' : record.action === 'create' ? '+' : '~',
      file_type: (data.file_type as string) || tGlobal('未知'),
      file_name: (data.file_name as string) || tGlobal('未知文件'),
      file_size: Number(data.file_size) || 0,
      source_type: data.source_type as 'upload' | 'git' | 'select',
      git_url: data.git_url as string | undefined,
      git_branch: data.git_branch as string | undefined,
      git_commit: data.git_commit as string | undefined,
      git_tag: data.git_tag as string | undefined,
      file_source: data.file_source as string | undefined,
    }];
  }

  // 处理包级别/对外释放文件级别操作日志
  if (record.target_type !== 'package' && record.target_type !== 'external_release') return [];
  if (!['create', 'update'].includes(record.action)) return [];

  const beforeFiles = (record.before_data?.files as any[]) || [];
  const afterFiles = (record.after_data?.files as any[]) || [];

  const changes: FileChange[] = [];

  // 对于 create 操作，所有文件都是新增
  if (record.action === 'create') {
    // files 数组中的文件
    afterFiles.forEach((file, index) => {
      const sourceType = file.source_type || 'upload';
      changes.push({
        id: `create-${getFileTypeName(file)}-${file.file_name || 'unknown'}-${index}`,
        action: '+',
        file_type: getFileTypeName(file),
        file_name: file.file_name || '-',
        file_size: file.file_size || 0,
        source_type: sourceType,
        git_url: file.git_url,
        git_branch: file.git_branch,
        git_commit: file.git_commit,
        git_tag: file.git_tag,
        file_source: file.file_source as string | undefined,
      });
    });

    // Release Note（文件和文字内容独立生成行）
    if (record.after_data?.release_note_url || record.after_data?.release_note_name) {
      changes.push({
        id: `create-release-note-file-${afterFiles.length}`,
        action: '+',
        file_type: 'Release Note',
        file_name: (record.after_data.release_note_name as string) || 'Release Note',
        file_size: Number(record.after_data.release_note_size) || 0,
        file_source: 'supplier',
      });
    }
    if (record.after_data?.release_note_content) {
      changes.push({
        id: `create-release-note-content-${afterFiles.length}`,
        action: '+',
        file_type: 'Release Note',
        file_name: tGlobal('Release Note (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: undefined,
        content_after: record.after_data.release_note_content as string,
      });
    }

    // 测试报告（文件和文字内容独立生成行）
    if (record.after_data?.test_report_url || record.after_data?.test_report_name) {
      changes.push({
        id: `create-test-report-file-${afterFiles.length + 1}`,
        action: '+',
        file_type: tGlobal('测试报告'),
        file_name: (record.after_data.test_report_name as string) || tGlobal('测试报告'),
        file_size: Number(record.after_data.test_report_size) || 0,
        file_source: 'supplier',
      });
    }
    if (record.after_data?.test_report_content) {
      changes.push({
        id: `create-test-report-content-${afterFiles.length + 1}`,
        action: '+',
        file_type: tGlobal('测试报告'),
        file_name: tGlobal('测试报告 (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: undefined,
        content_after: record.after_data.test_report_content as string,
      });
    }

    // Momenta Release Note（文件和文字内容独立生成行）
    if (record.after_data?.momenta_release_note_url || record.after_data?.momenta_release_note_name) {
      changes.push({
        id: `create-momenta-release-note-file-${afterFiles.length + 2}`,
        action: '+',
        file_type: 'Momenta Release Note',
        file_name: (record.after_data.momenta_release_note_name as string) || 'Momenta Release Note',
        file_size: Number(record.after_data.momenta_release_note_size) || 0,
        file_source: 'momenta',
      });
    }
    if (record.after_data?.momenta_release_note_content) {
      changes.push({
        id: `create-momenta-release-note-content-${afterFiles.length + 2}`,
        action: '+',
        file_type: 'Momenta Release Note',
        file_name: tGlobal('Momenta Release Note (文字内容)'),
        file_size: 0,
        file_source: 'momenta',
        content_before: undefined,
        content_after: record.after_data.momenta_release_note_content as string,
      });
    }

    // Momenta 测试报告（文件和文字内容独立生成行）
    if (record.after_data?.momenta_test_report_url || record.after_data?.momenta_test_report_name) {
      changes.push({
        id: `create-momenta-test-report-file-${afterFiles.length + 3}`,
        action: '+',
        file_type: tGlobal('Momenta测试报告'),
        file_name: (record.after_data.momenta_test_report_name as string) || tGlobal('Momenta测试报告'),
        file_size: Number(record.after_data.momenta_test_report_size) || 0,
        file_source: 'momenta',
      });
    }
    if (record.after_data?.momenta_test_report_content) {
      changes.push({
        id: `create-momenta-test-report-content-${afterFiles.length + 3}`,
        action: '+',
        file_type: tGlobal('Momenta测试报告'),
        file_name: tGlobal('Momenta测试报告 (文字内容)'),
        file_size: 0,
        file_source: 'momenta',
        content_before: undefined,
        content_after: record.after_data.momenta_test_report_content as string,
      });
    }

    return changes;
  }

  // 对于 update 操作，比较前后文件
  // 使用 checksum 作为唯一标识（如果没有则使用 file_name）
  const getFileKey = (f: any) => {
    const key = f.checksum || f.file_name;
    return key ? String(key).trim() : '';
  };

  // 创建文件映射
  const beforeMap = new Map(
    beforeFiles
      .filter(f => getFileKey(f))
      .map(f => [getFileKey(f), f])
  );
  const afterMap = new Map(
    afterFiles
      .filter(f => getFileKey(f))
      .map(f => [getFileKey(f), f])
  );

  // 查找新增的文件
  afterFiles.forEach((file, index) => {
    const key = getFileKey(file);
    if (key && !beforeMap.has(key)) {
      const sourceType = file.source_type || 'upload';
      changes.push({
        id: `update-add-${getFileTypeName(file)}-${file.file_name || 'unknown'}-${index}`,
        action: '+',
        file_type: getFileTypeName(file),
        file_name: file.file_name || '-',
        file_size: file.file_size || 0,
        source_type: sourceType,
        git_url: file.git_url,
        git_branch: file.git_branch,
        git_commit: file.git_commit,
        git_tag: file.git_tag,
        file_source: file.file_source as string | undefined,
      });
    }
  });

  // 查找删除的文件
  beforeFiles.forEach((file, index) => {
    const key = getFileKey(file);
    if (key && !afterMap.has(key)) {
      const sourceType = file.source_type || 'upload';
      changes.push({
        id: `update-del-${getFileTypeName(file)}-${file.file_name || 'unknown'}-${index}`,
        action: '-',
        file_type: getFileTypeName(file),
        file_name: file.file_name || '-',
        file_size: file.file_size || 0,
        source_type: sourceType,
        git_url: file.git_url,
        git_branch: file.git_branch,
        git_commit: file.git_commit,
        git_tag: file.git_tag,
        file_source: file.file_source as string | undefined,
      });
    }
  });

  // 补充：用后端记录的 deleted_files 确保明确删除的 PackageFile 被展示（防止 files 数组 diff 遗漏）
  const deletedFilesSupp: any[] = (record.after_data as any)?.deleted_files || [];
  deletedFilesSupp.forEach((df: any) => {
    if (!df.file_id) return;  // 跳过 doc type（test_report 等由下方逻辑处理）
    const alreadyShown = changes.some(c => c.action === '-' && c.file_name === df.file_name);
    if (!alreadyShown) {
      changes.push({
        id: `del-supplemented-${df.file_id}`,
        action: '-',
        file_type: df.file_type || '',
        file_name: df.file_name || '-',
        file_size: 0,
        file_source: df.file_source,
      });
    }
  });

  // 检查 Release Note 变更（支持平级和嵌套两种格式）
  // 注意：文件和文字内容独立检测

  // 1. 检测文件变化
  const hasBeforeRNFile_detail = Boolean(
    (record.before_data?.release_note_url && String(record.before_data.release_note_url).trim())
    || (record.before_data?.release_note_name && String(record.before_data.release_note_name).trim())
    || ((record.before_data as any)?.release_note?.release_note_url && String((record.before_data as any).release_note.release_note_url).trim())
    || ((record.before_data as any)?.release_note?.release_note_name && String((record.before_data as any).release_note.release_note_name).trim())
  );

  const hasAfterRNFile_detail = Boolean(
    (record.after_data?.release_note_url && String(record.after_data.release_note_url).trim())
    || (record.after_data?.release_note_name && String(record.after_data.release_note_name).trim())
    || ((record.after_data as any)?.release_note?.release_note_url && String((record.after_data as any).release_note.release_note_url).trim())
    || ((record.after_data as any)?.release_note?.release_note_name && String((record.after_data as any).release_note.release_note_name).trim())
  );

  // 新增文件
  if (!hasBeforeRNFile_detail && hasAfterRNFile_detail) {
    const rnData: any = record.after_data?.release_note || record.after_data;
    changes.push({
      id: `update-add-release-note-file`,
      action: '+' as const,
      file_type: 'Release Note',
      file_name: (rnData.release_note_name as string) || 'Release Note',
      file_size: Number(rnData.release_note_size) || 0,
      file_source: 'supplier',
    });
  }

  // 删除文件
  if (hasBeforeRNFile_detail && !hasAfterRNFile_detail) {
    const rnData: any = record.before_data?.release_note || record.before_data;
    changes.push({
      id: `update-del-release-note-file`,
      action: '-' as const,
      file_type: 'Release Note',
      file_name: (rnData.release_note_name as string) || 'Release Note',
      file_size: Number(rnData.release_note_size) || 0,
      file_source: 'supplier',
    });
  }

  // 替换文件：删旧+添新（文件名不同）
  if (hasBeforeRNFile_detail && hasAfterRNFile_detail) {
    const beforeRNName = record.before_data?.release_note_name || record.before_data?.release_note_url || (record.before_data?.release_note as any)?.release_note_name || '';
    const afterRNName = record.after_data?.release_note_name || record.after_data?.release_note_url || (record.after_data?.release_note as any)?.release_note_name || '';
    if (beforeRNName !== afterRNName) {
      changes.push({
        id: `update-del-release-note-file-replaced`,
        action: '-' as const,
        file_type: 'Release Note',
        file_name: String(beforeRNName) || 'Release Note',
        file_size: Number(record.before_data?.release_note_size) || 0,
        file_source: 'supplier',
      });
      changes.push({
        id: `update-add-release-note-file-replaced`,
        action: '+' as const,
        file_type: 'Release Note',
        file_name: String(afterRNName) || 'Release Note',
        file_size: Number(record.after_data?.release_note_size) || 0,
        file_source: 'supplier',
      });
    }
  }

  // 2. 检测文字内容变化（独立检测，不受文件状态影响）
  // 注意：对于专门的删除操作（嵌套格式），跳过文字内容检测
  const isNestedDeletionFormat = record.before_data?.release_note &&
    typeof record.before_data.release_note === 'object' &&
    !record.after_data?.release_note;

  if (!isNestedDeletionFormat) {
    const hasBeforeRNContent_detail = Boolean(
      (record.before_data?.release_note_content && String(record.before_data.release_note_content).trim())
    );

    const hasAfterRNContent_detail = Boolean(
      (record.after_data?.release_note_content && String(record.after_data.release_note_content).trim())
    );

    // 提取文字内容
    const getRNContent = (data: any) => {
      return data?.release_note_content || '';
    };

    const beforeRNContent = getRNContent(record.before_data);
    const afterRNContent = getRNContent(record.after_data);

    // 新增文字内容
    if (!hasBeforeRNContent_detail && hasAfterRNContent_detail) {
      changes.push({
        id: `update-add-release-note-content`,
        action: '+' as const,
        file_type: 'Release Note',
        file_name: tGlobal('Release Note (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: undefined,
        content_after: afterRNContent,
      });
    }

    // 删除文字内容
    if (hasBeforeRNContent_detail && !hasAfterRNContent_detail) {
      changes.push({
        id: `update-del-release-note-content`,
        action: '-' as const,
        file_type: 'Release Note',
        file_name: tGlobal('Release Note (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: beforeRNContent,
        content_after: undefined,
      });
    }

    // 修改文字内容
    if (hasBeforeRNContent_detail && hasAfterRNContent_detail && beforeRNContent !== afterRNContent) {
      changes.push({
        id: `update-modify-release-note-content`,
        action: '~' as const,
        file_type: 'Release Note',
        file_name: tGlobal('Release Note (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: beforeRNContent,
        content_after: afterRNContent,
      });
    }
  }

  // 检查测试报告变更（支持平级和嵌套两种格式）
  // 注意：文件和文字内容独立检测

  // 1. 检测文件变化
  const trBeforeFiles = (record.before_data as any)?.test_report_files;
  const trAfterFiles = (record.after_data as any)?.test_report_files;
  if (Array.isArray(trBeforeFiles) || Array.isArray(trAfterFiles)) {
    // 多附件：按 url/name 做列表 diff
    const keyOf = (f: { url?: string; name?: string }) => f.url || f.name || '';
    const beforeMap = new Map(
      (trBeforeFiles || []).filter((f: any) => keyOf(f)).map((f: any) => [keyOf(f), f]),
    );
    const afterMap = new Map(
      (trAfterFiles || []).filter((f: any) => keyOf(f)).map((f: any) => [keyOf(f), f]),
    );
    afterMap.forEach((f: any) => {
      if (!beforeMap.has(keyOf(f))) {
        changes.push({
          id: `update-add-test-report-file-${keyOf(f)}`,
          action: '+' as const,
          file_type: tGlobal('测试报告'),
          file_name: f.name || tGlobal('测试报告'),
          file_size: Number(f.size) || 0,
          file_source: 'supplier',
        });
      }
    });
    beforeMap.forEach((f: any) => {
      if (!afterMap.has(keyOf(f))) {
        changes.push({
          id: `update-del-test-report-file-${keyOf(f)}`,
          action: '-' as const,
          file_type: tGlobal('测试报告'),
          file_name: f.name || tGlobal('测试报告'),
          file_size: Number(f.size) || 0,
          file_source: 'supplier',
        });
      }
    });
  } else {
  const hasBeforeTRFile_detail = Boolean(
    (record.before_data?.test_report_url && String(record.before_data.test_report_url).trim())
    || (record.before_data?.test_report_name && String(record.before_data.test_report_name).trim())
    || ((record.before_data as any)?.test_report?.test_report_url && String((record.before_data as any).test_report.test_report_url).trim())
    || ((record.before_data as any)?.test_report?.test_report_name && String((record.before_data as any).test_report.test_report_name).trim())
  );

  const hasAfterTRFile_detail = Boolean(
    (record.after_data?.test_report_url && String(record.after_data.test_report_url).trim())
    || (record.after_data?.test_report_name && String(record.after_data.test_report_name).trim())
    || ((record.after_data as any)?.test_report?.test_report_url && String((record.after_data as any).test_report.test_report_url).trim())
    || ((record.after_data as any)?.test_report?.test_report_name && String((record.after_data as any).test_report.test_report_name).trim())
  );

  // 新增文件
  if (!hasBeforeTRFile_detail && hasAfterTRFile_detail) {
    const trData: any = record.after_data?.test_report || record.after_data;
    changes.push({
      id: `update-add-test-report-file`,
      action: '+' as const,
      file_type: tGlobal('测试报告'),
      file_name: (trData.test_report_name as string) || tGlobal('测试报告'),
      file_size: Number(trData.test_report_size) || 0,
      file_source: 'supplier',
    });
  }

  // 删除文件
  if (hasBeforeTRFile_detail && !hasAfterTRFile_detail) {
    const trData: any = record.before_data?.test_report || record.before_data;
    changes.push({
      id: `update-del-test-report-file`,
      action: '-' as const,
      file_type: tGlobal('测试报告'),
      file_name: (trData.test_report_name as string) || tGlobal('测试报告'),
      file_size: Number(trData.test_report_size) || 0,
      file_source: 'supplier',
    });
  }

  // 替换文件：删旧+添新（文件名不同）
  if (hasBeforeTRFile_detail && hasAfterTRFile_detail) {
    const beforeTRName = record.before_data?.test_report_name || record.before_data?.test_report_url || (record.before_data?.test_report as any)?.test_report_name || '';
    const afterTRName = record.after_data?.test_report_name || record.after_data?.test_report_url || (record.after_data?.test_report as any)?.test_report_name || '';
    if (beforeTRName !== afterTRName) {
      changes.push({
        id: `update-del-test-report-file-replaced`,
        action: '-' as const,
        file_type: tGlobal('测试报告'),
        file_name: String(beforeTRName) || tGlobal('测试报告'),
        file_size: Number(record.before_data?.test_report_size) || 0,
        file_source: 'supplier',
      });
      changes.push({
        id: `update-add-test-report-file-replaced`,
        action: '+' as const,
        file_type: tGlobal('测试报告'),
        file_name: String(afterTRName) || tGlobal('测试报告'),
        file_size: Number(record.after_data?.test_report_size) || 0,
        file_source: 'supplier',
      });
    }
  }
  }

  // 2. 检测文字内容变化（独立检测，不受文件状态影响）
  // 注意：对于专门的删除操作（嵌套格式），跳过文字内容检测
  const isNestedTRDeletionFormat = record.before_data?.test_report &&
    typeof record.before_data.test_report === 'object' &&
    !record.after_data?.test_report;

  if (!isNestedTRDeletionFormat) {
    const hasBeforeTRContent_detail = Boolean(
      (record.before_data?.test_report_content && String(record.before_data.test_report_content).trim())
    );

    const hasAfterTRContent_detail = Boolean(
      (record.after_data?.test_report_content && String(record.after_data.test_report_content).trim())
    );

    // 提取文字内容
    const getTRContent = (data: any) => {
      return data?.test_report_content || '';
    };

    const beforeTRContent = getTRContent(record.before_data);
    const afterTRContent = getTRContent(record.after_data);

    // 新增文字内容
    if (!hasBeforeTRContent_detail && hasAfterTRContent_detail) {
      changes.push({
        id: `update-add-test-report-content`,
        action: '+' as const,
        file_type: tGlobal('测试报告'),
        file_name: tGlobal('测试报告 (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: undefined,
        content_after: afterTRContent,
      });
    }

    // 删除文字内容
    if (hasBeforeTRContent_detail && !hasAfterTRContent_detail) {
      changes.push({
        id: `update-del-test-report-content`,
        action: '-' as const,
        file_type: tGlobal('测试报告'),
        file_name: tGlobal('测试报告 (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: beforeTRContent,
        content_after: undefined,
      });
    }

    // 修改文字内容
    if (hasBeforeTRContent_detail && hasAfterTRContent_detail && beforeTRContent !== afterTRContent) {
      changes.push({
        id: `update-modify-test-report-content`,
        action: '~' as const,
        file_type: tGlobal('测试报告'),
        file_name: tGlobal('测试报告 (文字内容)'),
        file_size: 0,
        file_source: 'supplier',
        content_before: beforeTRContent,
        content_after: afterTRContent,
      });
    }
  }

  // 检查 Momenta Release Note 变更
  const hasBeforeMRNFile_detail = Boolean(
    (record.before_data?.momenta_release_note_url && String(record.before_data.momenta_release_note_url).trim())
    || (record.before_data?.momenta_release_note_name && String(record.before_data.momenta_release_note_name).trim())
  );
  const hasAfterMRNFile_detail = Boolean(
    (record.after_data?.momenta_release_note_url && String(record.after_data.momenta_release_note_url).trim())
    || (record.after_data?.momenta_release_note_name && String(record.after_data.momenta_release_note_name).trim())
  );

  if (!hasBeforeMRNFile_detail && hasAfterMRNFile_detail) {
    changes.push({
      id: `update-add-momenta-release-note-file`,
      action: '+' as const,
      file_type: 'Momenta Release Note',
      file_name: (record.after_data?.momenta_release_note_name as string) || 'Momenta Release Note',
      file_size: Number(record.after_data?.momenta_release_note_size) || 0,
      file_source: 'momenta',
    });
  }
  if (hasBeforeMRNFile_detail && !hasAfterMRNFile_detail) {
    changes.push({
      id: `update-del-momenta-release-note-file`,
      action: '-' as const,
      file_type: 'Momenta Release Note',
      file_name: (record.before_data?.momenta_release_note_name as string) || 'Momenta Release Note',
      file_size: Number(record.before_data?.momenta_release_note_size) || 0,
      file_source: 'momenta',
    });
  }

  if (hasBeforeMRNFile_detail && hasAfterMRNFile_detail) {
    const bMRNName = record.before_data?.momenta_release_note_name || record.before_data?.momenta_release_note_url || '';
    const aMRNName = record.after_data?.momenta_release_note_name || record.after_data?.momenta_release_note_url || '';
    if (bMRNName !== aMRNName) {
      changes.push({ id: `update-del-momenta-release-note-file-replaced`, action: '-' as const, file_type: 'Momenta Release Note', file_name: String(bMRNName) || 'Momenta Release Note', file_size: Number(record.before_data?.momenta_release_note_size) || 0, file_source: 'momenta' });
      changes.push({ id: `update-add-momenta-release-note-file-replaced`, action: '+' as const, file_type: 'Momenta Release Note', file_name: String(aMRNName) || 'Momenta Release Note', file_size: Number(record.after_data?.momenta_release_note_size) || 0, file_source: 'momenta' });
    }
  }

  const hasBeforeMRNContent_detail = Boolean(record.before_data?.momenta_release_note_content && String(record.before_data.momenta_release_note_content).trim());
  const hasAfterMRNContent_detail = Boolean(record.after_data?.momenta_release_note_content && String(record.after_data.momenta_release_note_content).trim());
  const beforeMRNContent = (record.before_data?.momenta_release_note_content as string) || '';
  const afterMRNContent = (record.after_data?.momenta_release_note_content as string) || '';

  if (!hasBeforeMRNContent_detail && hasAfterMRNContent_detail) {
    changes.push({ id: `update-add-momenta-release-note-content`, action: '+' as const, file_type: 'Momenta Release Note', file_name: tGlobal('Momenta Release Note (文字内容)'), file_size: 0, file_source: 'momenta', content_before: undefined, content_after: afterMRNContent });
  }
  if (hasBeforeMRNContent_detail && !hasAfterMRNContent_detail) {
    changes.push({ id: `update-del-momenta-release-note-content`, action: '-' as const, file_type: 'Momenta Release Note', file_name: tGlobal('Momenta Release Note (文字内容)'), file_size: 0, file_source: 'momenta', content_before: beforeMRNContent, content_after: undefined });
  }
  if (hasBeforeMRNContent_detail && hasAfterMRNContent_detail && beforeMRNContent !== afterMRNContent) {
    changes.push({ id: `update-modify-momenta-release-note-content`, action: '~' as const, file_type: 'Momenta Release Note', file_name: tGlobal('Momenta Release Note (文字内容)'), file_size: 0, file_source: 'momenta', content_before: beforeMRNContent, content_after: afterMRNContent });
  }

  // 检查 Momenta 测试报告变更
  const mtrDetailBefore = (record.before_data as any)?.momenta_test_report_files;
  const mtrDetailAfter = (record.after_data as any)?.momenta_test_report_files;
  const hasBeforeMTRFile_detail = Boolean(
    (record.before_data?.momenta_test_report_url && String(record.before_data.momenta_test_report_url).trim())
    || (record.before_data?.momenta_test_report_name && String(record.before_data.momenta_test_report_name).trim())
  );
  const hasAfterMTRFile_detail = Boolean(
    (record.after_data?.momenta_test_report_url && String(record.after_data.momenta_test_report_url).trim())
    || (record.after_data?.momenta_test_report_name && String(record.after_data.momenta_test_report_name).trim())
  );

  if (Array.isArray(mtrDetailBefore) || Array.isArray(mtrDetailAfter)) {
    const keyOf = (f: { url?: string; name?: string }) => f.url || f.name || '';
    const beforeMap = new Map(
      (mtrDetailBefore || []).filter((f: any) => keyOf(f)).map((f: any) => [keyOf(f), f]),
    );
    const afterMap = new Map(
      (mtrDetailAfter || []).filter((f: any) => keyOf(f)).map((f: any) => [keyOf(f), f]),
    );
    afterMap.forEach((f: any) => {
      if (!beforeMap.has(keyOf(f))) {
        changes.push({
          id: `update-add-momenta-test-report-file-${keyOf(f)}`,
          action: '+' as const, file_type: tGlobal('Momenta测试报告'),
          file_name: f.name || tGlobal('Momenta测试报告'),
          file_size: Number(f.size) || 0, file_source: 'momenta',
        });
      }
    });
    beforeMap.forEach((f: any) => {
      if (!afterMap.has(keyOf(f))) {
        changes.push({
          id: `update-del-momenta-test-report-file-${keyOf(f)}`,
          action: '-' as const, file_type: tGlobal('Momenta测试报告'),
          file_name: f.name || tGlobal('Momenta测试报告'),
          file_size: Number(f.size) || 0, file_source: 'momenta',
        });
      }
    });
  } else {
  if (!hasBeforeMTRFile_detail && hasAfterMTRFile_detail) {
    changes.push({
      id: `update-add-momenta-test-report-file`,
      action: '+' as const,
      file_type: tGlobal('Momenta测试报告'),
      file_name: (record.after_data?.momenta_test_report_name as string) || tGlobal('Momenta测试报告'),
      file_size: Number(record.after_data?.momenta_test_report_size) || 0,
      file_source: 'momenta',
    });
  }
  if (hasBeforeMTRFile_detail && !hasAfterMTRFile_detail) {
    changes.push({
      id: `update-del-momenta-test-report-file`,
      action: '-' as const,
      file_type: tGlobal('Momenta测试报告'),
      file_name: (record.before_data?.momenta_test_report_name as string) || tGlobal('Momenta测试报告'),
      file_size: Number(record.before_data?.momenta_test_report_size) || 0,
      file_source: 'momenta',
    });
  }

  if (hasBeforeMTRFile_detail && hasAfterMTRFile_detail) {
    const bMTRName = record.before_data?.momenta_test_report_name || record.before_data?.momenta_test_report_url || '';
    const aMTRName = record.after_data?.momenta_test_report_name || record.after_data?.momenta_test_report_url || '';
    if (bMTRName !== aMTRName) {
      changes.push({ id: `update-del-momenta-test-report-file-replaced`, action: '-' as const, file_type: tGlobal('Momenta测试报告'), file_name: String(bMTRName) || tGlobal('Momenta测试报告'), file_size: Number(record.before_data?.momenta_test_report_size) || 0, file_source: 'momenta' });
      changes.push({ id: `update-add-momenta-test-report-file-replaced`, action: '+' as const, file_type: tGlobal('Momenta测试报告'), file_name: String(aMTRName) || tGlobal('Momenta测试报告'), file_size: Number(record.after_data?.momenta_test_report_size) || 0, file_source: 'momenta' });
    }
  }
  }

  const hasBeforeMTRContent_detail = Boolean(record.before_data?.momenta_test_report_content && String(record.before_data.momenta_test_report_content).trim());
  const hasAfterMTRContent_detail = Boolean(record.after_data?.momenta_test_report_content && String(record.after_data.momenta_test_report_content).trim());
  const beforeMTRContent = (record.before_data?.momenta_test_report_content as string) || '';
  const afterMTRContent = (record.after_data?.momenta_test_report_content as string) || '';

  if (!hasBeforeMTRContent_detail && hasAfterMTRContent_detail) {
    changes.push({ id: `update-add-momenta-test-report-content`, action: '+' as const, file_type: tGlobal('Momenta测试报告'), file_name: tGlobal('Momenta测试报告 (文字内容)'), file_size: 0, file_source: 'momenta', content_before: undefined, content_after: afterMTRContent });
  }
  if (hasBeforeMTRContent_detail && !hasAfterMTRContent_detail) {
    changes.push({ id: `update-del-momenta-test-report-content`, action: '-' as const, file_type: tGlobal('Momenta测试报告'), file_name: tGlobal('Momenta测试报告 (文字内容)'), file_size: 0, file_source: 'momenta', content_before: beforeMTRContent, content_after: undefined });
  }
  if (hasBeforeMTRContent_detail && hasAfterMTRContent_detail && beforeMTRContent !== afterMTRContent) {
    changes.push({ id: `update-modify-momenta-test-report-content`, action: '~' as const, file_type: tGlobal('Momenta测试报告'), file_name: tGlobal('Momenta测试报告 (文字内容)'), file_size: 0, file_source: 'momenta', content_before: beforeMTRContent, content_after: afterMTRContent });
  }

  return changes;
};

export const OperationLog: React.FC<OperationLogProps> = ({
  logs,
  loading = false,
}) => {
  const { t } = useT();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpand = (record: OperationLogType) => {
    const key = record.id;
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys(expandedRowKeys.filter(k => k !== key));
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  };

  const columns: ColumnsType<OperationLogType> = [
    {
      title: t('时间'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => formatDate(date),
    },
    {
      title: t('操作'),
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string, record) => (
        <Tag color={actionColors[action] || 'default'}>
          {record.action_display}
        </Tag>
      ),
    },
    {
      title: t('操作人'),
      key: 'operator',
      width: 120,
      render: (_, record) => (
        <div>
          <Text>{record.operator?.username || t('系统')}</Text>
          <br />
          <Text type="secondary" className="text-xs">
            {record.operator_type_display}
          </Text>
        </div>
      ),
    },
    {
      title: t('来源'),
      key: 'source',
      width: 100,
      render: (_, record) => {
        if (!record.source) return <Text type="secondary">-</Text>;
        return (
          <Tag color={sourceColors[record.source] || 'default'}>
            {record.source_display || record.source}
          </Tag>
        );
      },
    },
    {
      title: t('操作描述'),
      key: 'description',
      render: (_, record) => {
        // Display description if available
        if (record.description) {
          const FORCED = '【强制上传】';
          if (record.description.startsWith(FORCED)) {
            return (
              <Text>
                <Tag color="red">{t('强制上传')}</Tag>
                {record.description.slice(FORCED.length)}
              </Text>
            );
          }
          return <Text>{record.description}</Text>;
        }

        // Otherwise, show changes
        if (!record.before_data && !record.after_data) {
          return <Text type="secondary">-</Text>;
        }

        return (
          <div className="max-h-40 overflow-auto">
            {record.action === 'create' && record.after_data && (
              <div>
                <Text type="secondary">{t('创建了新记录')}</Text>
              </div>
            )}
            {record.action === 'delete' && record.before_data && (
              <div>
                <Text type="secondary">{t('删除了记录')}</Text>
              </div>
            )}
            {record.action === 'update' && record.before_data && record.after_data && (
              <div>
                <Text type="secondary">{t('更新了记录')}</Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('IP 地址'),
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (ip: string) => ip || '-',
    },
    {
      title: t('文件变更'),
      key: 'file_changes',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const summary = getFileChangeSummary(record);
        if (!summary) return <Text type="secondary">-</Text>;

        const isExpanded = expandedRowKeys.includes(record.id);

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <div>
              {summary.added > 0 && (
                <Text type="success" strong>
                  +{summary.added}
                </Text>
              )}
              {summary.added > 0 && summary.deleted > 0 && ' '}
              {summary.deleted > 0 && (
                <Text type="danger" strong>
                  -{summary.deleted}
                </Text>
              )}
            </div>
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleExpand(record);
              }}
              style={{ padding: 0 }}
            >
              {isExpanded ? t('收起') : t('展开')}
            </Button>
          </div>
        );
      },
    },
  ];

  if (!loading && logs.length === 0) {
    return <Empty description={t('暂无操作日志')} />;
  }

  // 渲染文件变更详情
  const renderFileChangesDetail = (record: OperationLogType) => {
    const changes = getFileChanges(record);

    if (changes.length === 0) {
      <Text type="secondary">{t('暂无文件变更')}</Text>;
    }

    const detailColumns: ColumnsType<FileChange> = [
      {
        title: t('操作'),
        dataIndex: 'action',
        key: 'action',
        width: 60,
        align: 'center',
        render: (action: string) => {
          const color = action === '+' ? 'success' : 'danger';
          return <Text type={color} strong>{action}</Text>;
        },
      },
      {
        title: t('文件类型'),
        dataIndex: 'file_type',
        key: 'file_type',
        width: 100,
      },
      {
        title: t('文件来源'),
        dataIndex: 'file_source',
        key: 'file_source',
        width: 90,
        render: (file_source: string | undefined) => {
          if (!file_source) return <Text type="secondary">-</Text>;
          return (
            <Tag color={file_source === 'momenta' ? 'purple' : 'blue'}>
              {file_source === 'momenta' ? 'Momenta' : t('供应商')}
            </Tag>
          );
        },
      },
      {
        title: t('文件名'),
        dataIndex: 'file_name',
        key: 'file_name',
        render: (file_name: string, record: FileChange) => {
          // 辅助函数：截断内容
          const truncateContent = (content: string, maxLength: number): string => {
            if (!content) return '';
            if (content.length <= maxLength) return content;
            return content.substring(0, maxLength) + '...';
          };

          // If Git source, show Git information
          if (record.source_type === 'git' && record.git_url) {
            return (
              <div style={{ fontSize: '12px' }}>
                <div style={{ marginBottom: '4px' }}>
                  <Tag color="cyan">{t('Git源')}</Tag>
                </div>
                <div>
                  <Text type="secondary">{t('仓库:')}</Text>
                  <br />
                  <Text copyable style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                    {record.git_url}
                  </Text>
                </div>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary">{t('分支:')}</Text>
                  <Text code style={{ marginLeft: '4px' }}>
                    {record.git_branch}
                  </Text>
                </div>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary">Commit:</Text>
                  <Text code copyable style={{ marginLeft: '4px', fontSize: '11px' }}>
                    {record.git_commit}
                  </Text>
                </div>
                {record.git_tag && (
                  <div style={{ marginTop: '4px' }}>
                    <Text type="secondary">Tag:</Text>
                    <Text code style={{ marginLeft: '4px' }}>
                      {record.git_tag}
                    </Text>
                  </div>
                )}
              </div>
            );
          }

          // If has content changes, show before/after content
          if (record.content_before || record.content_after) {
            return (
              <div style={{ fontSize: '12px' }}>
                <div style={{ wordBreak: 'break-all' }}>{file_name}</div>
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '11px'
                }}>
                  {record.action === '+' && record.content_after && (
                    <div>
                      <Text type="success" strong>{t('新增内容:')}</Text>
                      <div style={{ marginTop: '4px', color: '#52c41a' }}>
                        {truncateContent(record.content_after, 200)}
                      </div>
                    </div>
                  )}
                  {record.action === '-' && record.content_before && (
                    <div>
                      <Text type="danger" strong>{t('删除内容:')}</Text>
                      <div style={{ marginTop: '4px', color: '#ff4d4f' }}>
                        {truncateContent(record.content_before, 200)}
                      </div>
                    </div>
                  )}
                  {record.action === '~' && record.content_before && record.content_after && (
                    <div>
                      <div>
                        <Text type="danger" strong>{t('修改前:')}</Text>
                        <div style={{ marginTop: '4px', color: '#ff4d4f' }}>
                          {truncateContent(record.content_before, 100)}
                        </div>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <Text type="success" strong>{t('修改后:')}</Text>
                        <div style={{ marginTop: '4px', color: '#52c41a' }}>
                          {truncateContent(record.content_after, 100)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Regular file (upload or select)
          return <div style={{ wordBreak: 'break-all' }}>{file_name}</div>;
        },
      },
      {
        title: t('文件大小'),
        dataIndex: 'file_size',
        key: 'file_size',
        width: 120,
        render: (size: number) => {
          if (!size) return '-';
          const mb = size / (1024 * 1024);
          return `${mb.toFixed(2)} MB`;
        },
      },
    ];

    return (
      <div style={{ padding: '8px 24px' }}>
        <Table<FileChange>
          columns={detailColumns}
          dataSource={changes}
          pagination={false}
          size="small"
          rowKey="id"
        />
      </div>
    );
  };

  return (
    <Table<OperationLogType>
      columns={columns}
      dataSource={logs}
      loading={loading}
      rowKey="id"
      pagination={{
        pageSize: 10,
        showSizeChanger: false,
        showTotal: (total) => t('共 {total} 条', { total }),
      }}
      size="small"
      expandable={{
        expandedRowRender: renderFileChangesDetail,
        expandedRowKeys: expandedRowKeys,
        onExpand: (expanded, record) => {
          if (expanded) {
            setExpandedRowKeys([...expandedRowKeys, record.id]);
          } else {
            setExpandedRowKeys(expandedRowKeys.filter(k => k !== record.id));
          }
        },
        rowExpandable: (record) => {
          const summary = getFileChangeSummary(record);
          return summary !== null && (summary.added > 0 || summary.deleted > 0 || summary.updated > 0);
        },
      }}
    />
  );
};

export default OperationLog;
